/**
 * AuditSkeletons Component
 *
 * Skeleton loading states for audit sidebar sections during reanalyze streaming.
 * These match the layout of the actual components for a smooth transition.
 */

import React from 'react';
import { Box, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// ============================================================================
// CPT Analysis Skeleton
// ============================================================================

/**
 * Skeleton for the CPT Code Analysis section
 * Matches the compact layout of the CPT analysis card in NoteAuditSidebar
 */
export const CPTAnalysisSkeleton: React.FC = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        mb: 1.5,
        p: 1.5,
        borderRadius: 1.5,
        bgcolor: theme.palette.grey[50],
        border: `1px solid ${theme.palette.grey[200]}`,
      }}
    >
      {/* Single row: Status + Codes */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Skeleton variant="circular"  sx={{ width: 16, height: 16 }}/>
        <Skeleton variant="text"  sx={{ width: 70, height: 16 }}/>
        <Skeleton variant="text"  sx={{ width: 50, height: 16 }}/>
        <Skeleton variant="text"  sx={{ width: 16, height: 16 }}/>
        <Skeleton variant="text"  sx={{ width: 50, height: 16 }}/>
      </Box>
    </Box>
  );
};

// ============================================================================
// Rule Card Skeleton
// ============================================================================

/**
 * Skeleton for a single rule card
 * Matches the compact layout of RuleCard in RulesAtRiskView
 */
export const RuleCardSkeleton: React.FC<{ showLabel?: boolean }> = ({
  showLabel = true,
}) => {
  return (
    <Box
      sx={{
        p: 1.5,
        mb: 1,
        borderRadius: 2,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'borderColors.lightprimary',
        '&:last-child': { mb: 0 },
      }}
    >
      {/* Row 1: Title + Badge */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Skeleton variant="text"  sx={{ width: "70%", height: 18 }}/>
        {showLabel && <Skeleton variant="rounded" sx={{width: 80, height: 20,  borderRadius: 1 }} />}
      </Box>

      {/* Row 2: Suggestion + Icons */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Skeleton variant="text"  sx={{ width: "60%", height: 14 }}/>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Skeleton variant="circular"  sx={{ width: 20, height: 20 }}/>
          <Skeleton variant="circular"  sx={{ width: 20, height: 20 }}/>
          <Skeleton variant="circular"  sx={{ width: 20, height: 20 }}/>
        </Box>
      </Box>
    </Box>
  );
};

// ============================================================================
// Rules At Risk Skeleton
// ============================================================================

/**
 * Skeleton for the Rules At Risk section
 * Shows skeleton tabs and multiple rule card skeletons
 */
export const RulesAtRiskSkeleton: React.FC<{ cardCount?: number }> = ({
  cardCount = 3,
}) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* Header + dropdown skeleton */}
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Skeleton variant="text" sx={{width: 200, height: 24,  mb: 0.25 }} />
        <Skeleton variant="text" sx={{width: 260, height: 16,  mb: 1.5 }} />
        <Skeleton variant="rounded" sx={{width: 160, height: 36,  borderRadius: 1.5 }} />
      </Box>

      {/* Rule cards skeleton */}
      <Box sx={{ p: 2, pt: 1 }}>
        {Array.from({ length: cardCount }).map((_, index) => (
          <RuleCardSkeleton key={index} showLabel={true} />
        ))}
      </Box>
    </Box>
  );
};

export default {
  CPTAnalysisSkeleton,
  RuleCardSkeleton,
  RulesAtRiskSkeleton,
};
